import React from 'react';
import { Box, Container, Typography, Paper, Divider } from '@mui/material';
import { PlayCircleOutline, Security } from '@mui/icons-material';
import { motion } from 'framer-motion';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';

const Privacy: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
>
      <Header />
      <Box sx={{ height: 72 }} />
      <Container maxWidth="md" sx={{ py: 8, flex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <PlayCircleOutline
              sx={{
                fontSize: { xs: 60, md: 80 },
                color: 'primary.main',
                mb: 3,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Privacy Policy
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
              <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
              Your Privacy Matters
            </Typography>
            
            <Typography paragraph>
              At AutoReelr, operated by Jonkai Ventures OÜ ("we", "our", or "us"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered content creation platform.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              1. Information We Collect
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Personal Information:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Account information (username, email address, password)</li>
              <li>Profile information (name, preferences, settings)</li>
              <li>Payment information (processed securely by our payment providers)</li>
              <li>Communication data (support tickets, feedback, correspondence)</li>
            </Typography>
            
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Usage Information:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Content you create, edit, or publish through our platform</li>
              <li>Platform usage analytics and performance metrics</li>
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Log data (access times, pages viewed, actions taken)</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              2. How We Use Your Information
            </Typography>
            <Typography paragraph>
              We use the collected information for the following purposes:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Provide and maintain our AI-powered content creation services</li>
              <li>Process your content through AI models (OpenAI GPT, Azure TTS)</li>
              <li>Publish your content to connected social media platforms</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Improve our services and develop new features</li>
              <li>Send important updates about your account and our services</li>
              <li>Ensure platform security and prevent fraudulent activities</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              3. Third-Party Services and Data Sharing
            </Typography>
            <Typography paragraph>
              We integrate with various third-party services to provide our platform functionality:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li><strong>OpenAI:</strong> Your prompts and generated content for script creation</li>
              <li><strong>Microsoft Azure:</strong> Text content for speech synthesis</li>
              <li><strong>Pexels:</strong> Search queries for stock footage retrieval</li>
              <li><strong>Social Media Platforms:</strong> Content publishing and analytics</li>
              <li><strong>Payment Processors:</strong> Billing and subscription management</li>
              <li><strong>Cloud Storage:</strong> Secure storage of your generated content</li>
            </Typography>
            <Typography paragraph>
              We do not sell your personal information to third parties. Data is shared only as necessary to provide our services or as required by law.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              4. Data Security and Protection
            </Typography>
            <Typography paragraph>
              We implement industry-standard security measures to protect your information:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Restricted access to personal information on a need-to-know basis</li>
              <li>Secure data centers with physical and network security</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              5. Data Retention and Deletion
            </Typography>
            <Typography paragraph>
              We retain your personal information only as long as necessary to provide our services and fulfill legal obligations:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Account information: Retained while your account is active</li>
              <li>Generated content: Retained for service provision and user access</li>
              <li>Analytics data: Aggregated and anonymized after 24 months</li>
              <li>Legal compliance data: Retained as required by applicable laws</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              6. Your Rights Under GDPR
            </Typography>
            <Typography paragraph>
              As an EU-based service, we comply with the General Data Protection Regulation (GDPR). You have the right to:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdraw consent:</strong> Revoke consent for data processing</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              7. Cookies and Tracking Technologies
            </Typography>
            <Typography paragraph>
              We use cookies and similar technologies to enhance your experience:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li><strong>Essential cookies:</strong> Required for platform functionality</li>
              <li><strong>Analytics cookies:</strong> Help us understand usage patterns</li>
              <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
            </Typography>
            <Typography paragraph>
              You can control cookie settings through your browser preferences.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              8. International Data Transfers
            </Typography>
            <Typography paragraph>
              While based in Estonia (EU), some of our service providers may be located outside the European Economic Area. We ensure adequate protection through:
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Standard Contractual Clauses (SCCs)</li>
              <li>Adequacy decisions by the European Commission</li>
              <li>Other appropriate safeguards under GDPR</li>
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              9. Children's Privacy
            </Typography>
            <Typography paragraph>
              AutoReelr is not intended for users under 16 years of age. We do not knowingly collect personal information from children under 16. If you become aware that a child has provided personal information, please contact us immediately.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              10. Changes to Privacy Policy
            </Typography>
            <Typography paragraph>
              We may update this Privacy Policy periodically to reflect changes in our practices or applicable laws. We will notify you of significant changes via email or prominent notice on our platform.
            </Typography>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              11. Contact Information
            </Typography>
            <Typography paragraph>
              For questions about this Privacy Policy or to exercise your rights, contact us at:
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary">
              <strong>Data Controller:</strong> Jonkai Ventures OÜ<br />
              <strong>Email:</strong> info@autoreelr.com<br />
              <strong>Address:</strong> Tartu mnt 67/1-13b, 10115, Harju maakond, Tallinn, Kesklinna linnaosa<br />
              <strong>Registry Code:</strong> 17322110 - Estonia EU
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
              We are committed to protecting your privacy and will respond to your inquiries within 30 days as required by GDPR.
            </Typography>
          </Paper>
        </motion.div>
      </Container>

      <Footer />
    </Box>
  );
};

export default Privacy;